import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  AlertCircle,
  CheckCircle,
  Trash2,
  Loader2,
  ExternalLink,
  Plus,
  Plug,
  Settings2,
  Key,
  Save,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { FinancialsCard } from '@/components/integrations/FinancialsCard';
import { Button } from '@/components/ui/Button';
import { settingsApi, pluginsApi, clickupApi, PluginMetadata, PluginInstallation } from '@/lib/api';
import { BasecampIntegration } from '@/components/integrations/BasecampIntegration';
import { useAuthStore } from '@/stores/auth.store';

// Slack logo SVG
const SlackLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

// Google Drive logo
const GoogleDriveLogo = () => (
  <svg viewBox="0 0 87.3 78" className="w-5 h-5">
    <path d="M6.6 66.85L3.85 72.15 27.65 78H86.4L83.65 72.45 58.55 66.85Z" fill="#0066DA" />
    <path d="M27.6 78L6.6 66.85 30.3 25.85 51.3 37Z" fill="#00AC47" />
    <path d="M57.65 0L30.3 25.85 51.3 37 78.65 11.15Z" fill="#EA4335" />
    <path d="M57.65 0L78.65 11.15 83.65 72.45 58.55 66.85Z" fill="#00832D" />
    <path d="M57.65 0L78.65 11.15 51.3 37 30.3 25.85Z" fill="#2684FC" />
    <path d="M27.6 78L58.55 66.85 51.3 37 6.6 66.85Z" fill="#FFBA00" />
  </svg>
);

// ClickUp logo
const ClickUpLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path
      d="M3 14.5L7.5 9.5L12 14.5L16.5 9.5L21 14.5"
      stroke="url(#cu-grad-integrations)"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="cu-grad-integrations" x1="3" y1="12" x2="21" y2="12" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7B68EE" />
        <stop offset="1" stopColor="#FF4F9A" />
      </linearGradient>
    </defs>
  </svg>
);

// Plugin icons map
const PLUGIN_ICONS: Record<string, React.ReactNode> = {
  slack: <SlackLogo />,
  'google-drive': <GoogleDriveLogo />,
};

// Plugin background colors
const PLUGIN_COLORS: Record<string, string> = {
  slack: 'bg-[#4A154B]/10 text-[#4A154B]',
  'google-drive': 'bg-accent/10 text-accent',
};

interface PluginCardProps {
  plugin: PluginMetadata;
  installation: PluginInstallation | null;
  onInstall: () => void;
  onUninstall: () => void;
  onOAuthConnect: () => void;
  onSaveConfig: (config: { clientId: string; clientSecret: string }) => Promise<void>;
  isInstalling: boolean;
  isUninstalling: boolean;
  isConnecting: boolean;
  isSavingConfig: boolean;
}

function PluginCard({
  plugin,
  installation,
  onInstall,
  onUninstall,
  onOAuthConnect,
  onSaveConfig,
  isInstalling,
  isUninstalling,
  isConnecting,
  isSavingConfig,
}: PluginCardProps) {
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);

  const isConnected = installation?.credentials?.hasAccessToken;
  const isInstalled = !!installation;
  const hasAppConfig = installation?.credentials?.hasAppConfig;
  const needsConfig = isInstalled && !isConnected && plugin.requiresOAuth && !hasAppConfig;

  const handleSaveConfig = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setConfigError('Both Client ID and Client Secret are required');
      return;
    }
    setConfigError(null);
    try {
      await onSaveConfig({ clientId: clientId.trim(), clientSecret: clientSecret.trim() });
      setClientId('');
      setClientSecret('');
      setShowConfigForm(false);
    } catch (err) {
      setConfigError((err as Error).message);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${PLUGIN_COLORS[plugin.id] || 'bg-grey-100 text-grey-600'}`}>
          {PLUGIN_ICONS[plugin.id] || <Plug className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <h3 className="text-heading-3 text-secondary">{plugin.name}</h3>
          <p className="text-sm text-grey-500">{plugin.description}</p>
        </div>
        {isConnected && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Connected
          </span>
        )}
        {isInstalled && !isConnected && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
            <AlertCircle className="w-3 h-3" />
            Setup Required
          </span>
        )}
      </div>

      {/* Not installed */}
      {!isInstalled && (
        <div className="space-y-4">
          {plugin.supportedEvents && plugin.supportedEvents.length > 0 && (
            <div className="p-4 bg-grey-50 rounded-lg">
              <p className="text-sm text-grey-600 mb-2">Supported events:</p>
              <div className="flex flex-wrap gap-1">
                {plugin.supportedEvents.slice(0, 5).map((event) => (
                  <span key={event} className="text-xs bg-white px-2 py-1 rounded border text-grey-600">
                    {event}
                  </span>
                ))}
                {plugin.supportedEvents.length > 5 && (
                  <span className="text-xs text-grey-500">
                    +{plugin.supportedEvents.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={onInstall}
            disabled={isInstalling}
          >
            {isInstalling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Install {plugin.name}
          </Button>
        </div>
      )}

      {/* Installed but needs app config first */}
      {needsConfig && (
        <div className="space-y-4">
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
            <Key className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-secondary mb-1">App Credentials Required</h4>
              <p className="text-sm text-grey-600">
                Enter your {plugin.name} app credentials to enable the OAuth connection.
              </p>
              {plugin.id === 'slack' && (
                <div className="mt-2 text-sm text-grey-600 space-y-2">
                  <p>
                    1. Create a Slack app at{' '}
                    <a
                      href="https://api.slack.com/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      api.slack.com/apps
                    </a>
                  </p>
                  <p>
                    2. Under <strong>OAuth & Permissions</strong>, add this Redirect URL:
                  </p>
                  <code className="block bg-grey-100 px-2 py-1 rounded text-xs break-all">
                    {window.location.hostname === 'localhost'
                      ? 'http://localhost:4001/api/v1/plugins/slack/oauth/callback'
                      : 'https://hub-api.soliddigital.com/api/v1/plugins/slack/oauth/callback'}
                  </code>
                  <p>
                    3. Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> from Basic Information.
                  </p>
                </div>
              )}
            </div>
          </div>

          {!showConfigForm ? (
            <div className="flex items-center gap-3">
              <Button onClick={() => setShowConfigForm(true)}>
                <Key className="w-4 h-4" />
                Configure App Credentials
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:bg-primary/5"
                onClick={onUninstall}
                disabled={isUninstalling}
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Enter Client ID"
                    className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Enter Client Secret"
                    className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
                  />
                </div>
              </div>

              {configError && (
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
                  {configError}
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button onClick={handleSaveConfig} disabled={isSavingConfig}>
                  {isSavingConfig ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Credentials
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowConfigForm(false);
                    setClientId('');
                    setClientSecret('');
                    setConfigError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Installed with app config but needs OAuth */}
      {isInstalled && !isConnected && plugin.requiresOAuth && hasAppConfig && (
        <div className="space-y-4">
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-secondary mb-1">Connection Required</h4>
              <p className="text-sm text-grey-600">
                Complete the OAuth flow to connect {plugin.name}.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={onOAuthConnect} disabled={isConnecting}>
              {isConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Connect {plugin.name}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:bg-primary/5"
              onClick={onUninstall}
              disabled={isUninstalling}
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* Connected */}
      {isConnected && (
        <div className="space-y-4">
          <div className="p-4 bg-grey-50 rounded-lg">
            {installation?.credentials?.teamName && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-secondary">Workspace</span>
                <code className="text-xs bg-white px-2 py-1 rounded border">
                  {installation.credentials.teamName}
                </code>
              </div>
            )}
            {installation?.credentials?.scope && (
              <p className="text-xs text-grey-500">
                Scopes: {installation.credentials.scope}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onOAuthConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Reconnect
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:bg-primary/5"
              onClick={onUninstall}
              disabled={isUninstalling}
            >
              <Trash2 className="w-4 h-4" />
              Disconnect
            </Button>
          </div>

          {/* Plugin-specific settings link */}
          {plugin.id === 'slack' && installation && (
            <div className="pt-4 border-t border-grey-100">
              <p className="text-sm text-grey-600">
                Configure which events are sent to Slack channels in the{' '}
                <Link
                  to={`/settings/integrations/slack/${installation.id}`}
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  <Settings2 className="w-3 h-3" />
                  Slack settings
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Google Drive uses a different flow (service account upload instead of OAuth)
function GoogleDriveCard() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: driveStatus, isLoading } = useQuery({
    queryKey: ['google-drive-status'],
    queryFn: () => settingsApi.getGoogleDriveStatus(),
  });

  const uploadMutation = useMutation({
    mutationFn: (credentialsJson: string) =>
      settingsApi.uploadGoogleDriveCredentials(credentialsJson),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['google-drive-status'] });
        queryClient.invalidateQueries({ queryKey: ['files-status'] });
        setUploadError(null);
      } else {
        setUploadError(data.error || 'Failed to upload credentials');
      }
    },
    onError: (error: Error) => {
      setUploadError(error.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => settingsApi.removeGoogleDriveCredentials(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-drive-status'] });
      queryClient.invalidateQueries({ queryKey: ['files-status'] });
    },
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      uploadMutation.mutate(content);
    } catch {
      setUploadError('Failed to read file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <GoogleDriveLogo />
        </div>
        <div className="flex-1">
          <h3 className="text-heading-3 text-secondary">Google Drive</h3>
          <p className="text-sm text-grey-500">
            Share files with clients through Google Drive
          </p>
        </div>
        {driveStatus?.configured && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Connected
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      ) : driveStatus?.configured ? (
        <div className="space-y-4">
          <div className="p-4 bg-grey-50 rounded-lg space-y-2">
            {driveStatus.serviceAccountEmail && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-secondary">Service Account</span>
                <code className="text-xs bg-white px-2 py-1 rounded border max-w-[280px] truncate">
                  {driveStatus.serviceAccountEmail}
                </code>
              </div>
            )}
            {driveStatus.projectId && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-secondary">Project ID</span>
                <code className="text-xs bg-white px-2 py-1 rounded border">
                  {driveStatus.projectId}
                </code>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              <Upload className="w-4 h-4" />
              Replace Credentials
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:bg-primary/5"
              onClick={() => removeMutation.mutate()}
              disabled={removeMutation.isPending}
            >
              <Trash2 className="w-4 h-4" />
              Disconnect
            </Button>
          </div>

          <div className="pt-4 border-t border-grey-100">
            <p className="text-sm text-grey-600">
              Configure Drive folders for each project in the project's <strong>Files</strong> tab.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-grey-50 rounded-lg">
            <p className="text-sm text-grey-600 mb-4">
              To connect Google Drive, upload a service account JSON key file from Google Cloud Console.
            </p>

            <ol className="list-decimal list-inside space-y-2 text-sm text-grey-600">
              <li>
                Go to{' '}
                <a
                  href="https://console.cloud.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Google Cloud Console
                </a>
              </li>
              <li>Create a project or select an existing one</li>
              <li>Enable the Google Drive API</li>
              <li>Create a service account and download the JSON key</li>
              <li>Share your Drive folders with the service account email</li>
            </ol>
          </div>

          {uploadError && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-primary mb-1">Error</h4>
                <p className="text-sm text-grey-600">{uploadError}</p>
              </div>
            </div>
          )}

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Connect Google Drive
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFileSelect}
        className="hidden"
      />
    </Card>
  );
}

function ClickUpCard() {
  const { user } = useAuthStore();
  const agencyOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY');
  const organizationId = (agencyOrg as any)?.org_id || agencyOrg?.id;

  const { data: config, isLoading } = useQuery({
    queryKey: ['clickup-config', organizationId],
    queryFn: () => clickupApi.getConfig(organizationId!),
    enabled: !!organizationId,
  });

  const isConfigured = !!(config?.defaultListId);

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <ClickUpLogo />
        </div>
        <div className="flex-1">
          <h3 className="text-heading-3 text-secondary">ClickUp</h3>
          <p className="text-sm text-grey-500">
            Create ClickUp tickets directly from Slack triage alerts
          </p>
        </div>
        {isLoading ? null : isConfigured ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
            <AlertCircle className="w-3 h-3" />
            Setup Required
          </span>
        )}
      </div>

      <div className="pt-4 border-t border-grey-100">
        <p className="text-sm text-grey-600">
          {isConfigured
            ? 'Configure list IDs per project in the '
            : 'Set up your ClickUp list ID in the '}
          <Link
            to="/settings/integrations/clickup"
            className="text-accent hover:underline inline-flex items-center gap-1"
          >
            <Settings2 className="w-3 h-3" />
            ClickUp settings
          </Link>
          {isConfigured ? '.' : ' page.'}
        </p>
      </div>
    </Card>
  );
}

export function SettingsIntegrations() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [installingPluginId, setInstallingPluginId] = useState<string | null>(null);
  const [uninstallingPluginId, setUninstallingPluginId] = useState<string | null>(null);
  const [connectingPluginId, setConnectingPluginId] = useState<string | null>(null);
  const [savingConfigPluginId, setSavingConfigPluginId] = useState<string | null>(null);

  // Get agency organization ID
  const agencyOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY');
  const organizationId = (agencyOrg as any)?.org_id || agencyOrg?.id;

  // Get all available plugins
  const { data: pluginsData, isLoading: pluginsLoading, error: pluginsError } = useQuery({
    queryKey: ['plugins'],
    queryFn: () => pluginsApi.listPlugins(),
  });

  // Get installations for each plugin
  const { data: slackInstallations } = useQuery({
    queryKey: ['plugin-installations', 'slack', organizationId],
    queryFn: () => pluginsApi.listInstallations('slack', organizationId!),
    enabled: !!organizationId,
  });

  // Filter out google-drive from plugin list (it has its own card with different flow)
  const plugins = pluginsData?.plugins?.filter((p) => p.id !== 'google-drive') || [];

  // Get installation for a plugin
  const getInstallation = (pluginId: string): PluginInstallation | null => {
    if (pluginId === 'slack') {
      return slackInstallations?.[0] || null;
    }
    return null;
  };

  // Install plugin mutation
  const installMutation = useMutation({
    mutationFn: async (pluginId: string) => {
      setInstallingPluginId(pluginId);
      return pluginsApi.install(pluginId, organizationId!);
    },
    onSuccess: async (_data, pluginId) => {
      queryClient.invalidateQueries({ queryKey: ['plugin-installations', pluginId] });
      // After installing, the user will need to configure app credentials
      // before connecting via OAuth. The UI will guide them through this.
    },
    onSettled: () => {
      setInstallingPluginId(null);
    },
  });

  // Uninstall plugin mutation
  const uninstallMutation = useMutation({
    mutationFn: async ({ pluginId, installationId }: { pluginId: string; installationId: string }) => {
      setUninstallingPluginId(pluginId);
      return pluginsApi.uninstall(pluginId, installationId);
    },
    onSuccess: (_, { pluginId }) => {
      queryClient.invalidateQueries({ queryKey: ['plugin-installations', pluginId] });
    },
    onSettled: () => {
      setUninstallingPluginId(null);
    },
  });

  // OAuth connect mutation
  const connectMutation = useMutation({
    mutationFn: async ({ pluginId, installationId }: { pluginId: string; installationId: string }) => {
      setConnectingPluginId(pluginId);
      return pluginsApi.getOAuthUrl(pluginId, installationId);
    },
    onSuccess: (data) => {
      window.location.href = data.authorizationUrl;
    },
    onError: () => {
      setConnectingPluginId(null);
    },
  });

  // Save app config mutation
  const saveConfigMutation = useMutation({
    mutationFn: async ({
      pluginId,
      installationId,
      config,
    }: {
      pluginId: string;
      installationId: string;
      config: { clientId: string; clientSecret: string };
    }) => {
      setSavingConfigPluginId(pluginId);
      return pluginsApi.saveAppConfig(pluginId, installationId, config);
    },
    onSuccess: (_, { pluginId }) => {
      queryClient.invalidateQueries({ queryKey: ['plugin-installations', pluginId] });
    },
    onSettled: () => {
      setSavingConfigPluginId(null);
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-heading-3 text-secondary">Integrations</h2>
        <p className="text-sm text-grey-500 mt-1">
          Connect external services to enhance your workflow
        </p>
      </div>

      {/* Outside the plugin-registry branch on purpose. This card reads its own
          endpoint and knows nothing about the registry, so a registry failure
          must not hide it — that is exactly what happened the first time, and
          the one integration somebody had come here to fix was the one that
          disappeared. */}
      <FinancialsCard />

      {pluginsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
        </div>
      ) : pluginsError ? (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-error" />
            <div>
              <h3 className="mb-1 font-medium text-secondary">Couldn't load integrations</h3>
              <p className="text-sm text-grey-600">
                {(pluginsError as Error).message || 'The plugin list could not be reached.'}
              </p>
              <button
                type="button"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['plugins'] })}
                className="mt-3 text-sm font-medium text-accent hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* Dynamic plugin cards */}
          {plugins.length === 0 && (
            <Card className="p-6">
              <div className="flex items-start gap-3 text-grey-500">
                <Plug className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-secondary mb-1">No notification plugins available</h3>
                  <p className="text-sm">
                    No plugins are registered. Contact an administrator if you expected to see plugins here.
                  </p>
                </div>
              </div>
            </Card>
          )}
          {plugins.map((plugin) => {
            const installation = getInstallation(plugin.id);
            return (
              <PluginCard
                key={plugin.id}
                plugin={plugin}
                installation={installation}
                onInstall={() => installMutation.mutate(plugin.id)}
                onUninstall={() =>
                  installation && uninstallMutation.mutate({ pluginId: plugin.id, installationId: installation.id })
                }
                onOAuthConnect={() =>
                  installation && connectMutation.mutate({ pluginId: plugin.id, installationId: installation.id })
                }
                onSaveConfig={async (config) => {
                  if (!installation) return;
                  await saveConfigMutation.mutateAsync({
                    pluginId: plugin.id,
                    installationId: installation.id,
                    config,
                  });
                }}
                isInstalling={installingPluginId === plugin.id}
                isUninstalling={uninstallingPluginId === plugin.id}
                isConnecting={connectingPluginId === plugin.id}
                isSavingConfig={savingConfigPluginId === plugin.id}
              />
            );
          })}

          {/* Google Drive (special card with service account flow) */}
          <GoogleDriveCard />

          {/* ClickUp Integration */}
          <ClickUpCard />

          {/* Basecamp Integration (legacy) */}
          <BasecampIntegration />
        </>
      )}
    </div>
  );
}
